import React, { useCallback } from "react";
import "./i18n";
import { IModalInstance } from "@whatslly/ui-components/modals/ModalProvider";
import { BaseModal } from "@whatslly/ui-components/modals";
import type { LoginProviderType } from "@whatslly/types/LoginProviderType";
import { AppIds } from "@whatslly/types/LoginProviderType";
import SalesforceLoginForm, {
  EtokenExpiredType,
} from "@whatslly/ui-components/Pages/SalesforceLogin/components/SalesforceLoginForm/SalesforceLoginForm";
import { useTranslation } from "react-i18next";
import styles from "./etoken-expired-modal.module.scss";
import MicrosoftDynamicsLoginForm from "@whatslly/ui-components/Pages/MicrosoftDynamicsLogin/components/MicrosoftDynamicsLoginForm/MicrosoftDynamicsLoginForm";
import { Svg } from "@whatslly/ui-components/v2/BaseElements/Svg";

export type EtokenExpiredModalProps = {
  loginProvider: LoginProviderType;
  etokenExpired?: EtokenExpiredType;
};

export const EtokenExpired: React.FC<EtokenExpiredModalProps & IModalInstance<boolean>> = ({ loginProvider, etokenExpired, closeModal }) => {
  const { t } = useTranslation();

  const Message = useCallback(() => {
    return (
      <div className={styles.message}>
        <div>{t("etoken_expired_modal.message_text_top", { loginProvider })}</div>
        <span>{t("etoken_expired_modal.message_text_bottom", { loginProvider })}</span>
      </div>
    );
  }, [loginProvider, t]);

  return (
    <BaseModal
      closeModal={() => closeModal(false)}
      closeOnClickOutside={false}
      className={styles.etokenExpiredModal}
      bodyClassName={styles.etokenExpiredModalBody}
    >
      <div className={styles.container}>
        <div className={styles.logoContainer}>
          <Svg iconName="tuvis-icon-with-warning" height={73} width={73} />
        </div>
        <div>
          {loginProvider === AppIds.SALESFORCE ? (
            <SalesforceLoginForm
              onLogin={() => closeModal(true)}
              customSettings={{
                customDomain: etokenExpired?.base,
                secondButton: {
                  link: true,
                  text: etokenExpired?.logoutText ?? t("etoken_expired_modal.logout"),
                  onClick: () => {
                    closeModal(false);
                  },
                  className: styles.logoutButton,
                },
                hideInputs: true,
                isCustomDomain: true,
                callToActionText: t("etoken_expired_modal.need_to_change_info", { loginProvider }),
                connectButtonText: t("etoken_expired_modal.reconnect"),
                hideFooter: true,
                connectButtonClassName: styles.connectButton,
                message: <Message />,
              }}
            />
          ) : (
            <MicrosoftDynamicsLoginForm
              onLogin={() => closeModal(true)}
              customSettings={{
                customDomain: etokenExpired?.base,
                secondButton: {
                  link: true,
                  text: etokenExpired?.logoutText,
                  onClick: () => {
                    closeModal(false);
                  },
                },
                hideInputs: true,
                callToActionText: t("etoken_expired_modal.need_to_change_info", { loginProvider }),
                connectButtonText: t("etoken_expired_modal.reconnect"),
                hideFooter: true,
                message: <Message />,
                connectButtonClassName: styles.connectButton,
              }}
            />
          )}
        </div>
      </div>
    </BaseModal>
  );
};
